<script setup>
import {computed, onMounted, ref} from "vue";
import {useStore} from "vuex";

import DefaultButton from "@/components/onsiteEditor/components/buttons/DefaultButton.vue";
import DowngradeOptimiserModal from "@/components/onsiteEditor/components/modals/DowngradeOptimiserModal.vue";
import AgencyWarningModal from "../modals/AgencyWarningModal.vue";

const store = useStore();

const props = defineProps({
  websiteId: {
    type: [Number, null],
    required: false,
    default: null,
  },
  encodedWebsiteId: {
    type: [String, null],
    required: false,
    default: null,
  },
  isReseller: {
    type: Boolean,
    required: true,
  },
  fullPausedPrice: {
    type: String,
    required: true,
  },
  showVat: {
    type: Boolean,
    required: true,
  },
});

const completedActionsTimeInMinutes = ref(0);
const showDowngradeOptimiserModal = ref(false);

onMounted(async () => completedActionsTimeInMinutes.value = await store.dispatch(
        'onsiteEditor/fetchCompletedActionsTime', props.websiteId));

const calculateTime = (value) => {
    if (value < 60) {
        return `${value} minute${value === 1 ? '' : 's'}`
    }
    const hours = Math.round(value/60)

    return `${hours} hour${hours === 1 ? '' : 's'}`
};

const completedActionsTimeText = computed(() => calculateTime(completedActionsTimeInMinutes.value));

const goBack = () => window.location.href = props.isReseller ?
    `/website/settings/${props.encodedWebsiteId}`
    : '/settings#account';

const goToNextStep = () => window.location.href = props.isReseller ?
    `/agency/website/delete/${props.encodedWebsiteId}`
    : '/settings/cancel';

onMounted(() => store.dispatch('user/getUser'))
const user = computed(() => store.getters['user/user'])
const showAgencyWarningModal = ref(false)

const preHandleDeleteWebsite = () => {
  if (user.value.organisation_customer_type === 'AGENCY') {
    showAgencyWarningModal.value = true
    return
  }
  handleDeleteWebsite()
}
const handleDeleteWebsite = () => {
  window.mixpanel('Optimizer: Remove Confirmation');

  goToNextStep();
}
</script>

<template>
  <div
    v-if="user"
    class="tw-flex tw-flex-col"
  >
    <div class="tw-mb-5 tw-text-4xl tw-font-bold tw-leading-9 tw-text-slate-100">
      Are You Sure You Want to Say Goodbye to the Onsite Optimizer?
    </div>
    <div class="tw-flex tw-flex-col tw-gap-5 tw-text-base tw-leading-7 tw-text-slate-200">
      <div>
        <!-- Merchant Center SME / Single / Appsumo -->
        <div v-if="['MERCHANT_CUSTOMER','SINGLE','APPSUMO'].includes(user.organisation_customer_type)">
          <span class="text-bold">Important Notice:</span>
          You are currently using the Onsite Optimizer on your website. If you proceed with account cancellation,
          the changes made via the Onsite Optimizer will be removed from your website.
        </div>

        <!-- Agency -->
        <div v-else>
          <span class="text-bold">Important Notice:</span>
          You are actively using the Onsite Optimizer for this website. Removing the website from your account will
          disable your ability to make new edits or amend existing changes. However, all changes you've implemented
          will remain effective as long as your Hike Agency account is active.
        </div>
      </div>

      <div>
        <span class="text-bold">Consider Pausing Your Account Instead:</span><br> For just {{ fullPausedPrice }} per month, you can pause your account. This option maintains your access to the Onsite Optimizer, allowing you to continue making crucial edits and adjustments as needed. Please note: Full functionality, including our AI-driven content creation and the actions panel, requires an active account.
      </div>

      <div>
        <!-- Merchant Center SME / Single / Appsumo -->
        <div v-if="['MERCHANT_CUSTOMER','SINGLE','APPSUMO'].includes(user.organisation_customer_type)">
          <span class="text-bold">Still Considering Cancellation?</span><br>
          We respect your decision to move on. If you decide to cancel, we strongly suggest you download a CSV
          file of all SEO adjustments you've made by clicking the link below. This record helps you or your web
          developer manually reimplement these changes if necessary.
        </div>

        <!-- Agency -->
        <div v-else>
          <span class="text-bold">Still Considering Cancellation?</span><br>
          As long as you maintain your Hike Agency account, any changes made via the Onsite Optimizer will stay
          intact on the client's site, provided the script remains installed. If you decide to remove the script,
          we strongly suggest you download a CSV file of all SEO adjustments you've made. This record will assist
          you or your client in manually reimplementing these changes if necessary.
        </div>
      </div>
      <div>
        <span class="text-bold">Estimated Time to Manually Reimplement:</span> <span class="tw-font-bold tw-text-matterhorn">{{ completedActionsTimeText }}</span>.
      </div>
      <div>
        <DefaultButton
          class="!tw-w-68 "
          variant="info"
          @click="store.dispatch('onsiteEditor/fetchChangesCsv', websiteId)"
        >
          Download CSV File
        </DefaultButton>
      </div>
    </div>
    <div class="tw-mt-12 tw-flex tw-justify-between">
      <DefaultButton
        class="!tw-w-[189px]"
        @click="goBack"
      >
        Go Back
      </DefaultButton>
      <div class="tw-flex tw-gap-3">
        <DefaultButton
          class="!tw-w-68"
          variant="info"
          @click="showDowngradeOptimiserModal = true"
        >
          Pause Account
        </DefaultButton>
        <DefaultButton
          class="!tw-w-68"
          variant="danger"
          @click="preHandleDeleteWebsite"
        >
          {{ props.isReseller ? 'Delete Website' : 'Cancel Account' }}
        </DefaultButton>
      </div>

      <DowngradeOptimiserModal
        v-if="showDowngradeOptimiserModal"
        :full-paused-price="fullPausedPrice"
        :show-vat="showVat"
        @close-modal="showDowngradeOptimiserModal = false"
        @confirm="showDowngradeOptimiserModal = false; store.dispatch('onsiteEditor/downgradeWebsite', websiteId)"
      />

      <AgencyWarningModal
        v-if="showAgencyWarningModal"
        @close="showAgencyWarningModal = false"
        @confirm="handleDeleteWebsite"
      />
    </div>
  </div>
</template>
